import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  AppearanceContext,
  applyAppearance,
  directionFor,
  readStoredAppearance,
  writeStoredAppearance,
  type Appearance,
  type Accent,
  type AppearanceState,
  type Background,
  type Density,
  type Locale,
  type Theme,
} from './appearance';

/**
 * Owns the global appearance switches. Everything visual downstream is a token
 * remap: no component may ask which theme, density or direction is active.
 *
 * Deliberate non-features, each with a reason:
 *
 * - **No `prefers-color-scheme`.** The handoff fixes `theme`'s default at
 *   'dark' and makes it a persisted per-user preference. Seeding from the OS
 *   would mean a user who had never chosen anything gets flipped by a Windows
 *   update. Task 17's sheet carries no `@media` for the same reason, enforced
 *   by a test there. `<meta name="color-scheme" content="dark light">` in
 *   index.html plus `color-scheme` in reset.css still tell the browser to paint
 *   native scrollbars and form controls to match the chosen theme.
 * - **No `prefers-reduced-motion` handling here.** It is a CSS concern and
 *   reset.css already implements it globally; a JS mirror would be a second
 *   source of truth that drifts.
 * - **Not SSR-safe, by design.** This is a Vite SPA with no server renderer:
 *   `readStoredAppearance` touches `localStorage` in the state initialiser and
 *   the effect touches `document`. Guards for a renderer that does not exist
 *   would be untested code. If SSR ever arrives, the boot snippet in index.html
 *   is the piece that has to move, not this file.
 * - **No cross-tab sync.** A `storage` listener would let one tab repaint
 *   another mid-task. Agents work a queue; surprise repaints are worse than a
 *   stale second tab.
 */
export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppearanceState>(readStoredAppearance);

  // The value restored at mount. Persisting only once the user diverges from it
  // keeps an untouched account free to follow a future change of default,
  // instead of being pinned to today's by the mere act of loading the app.
  const restored = useRef(state);

  useEffect(() => {
    applyAppearance(state);
  }, [state]);

  useEffect(() => {
    if (state === restored.current) return;
    writeStoredAppearance(state);
  }, [state]);

  // Each setter is a no-op when the value is unchanged, so re-selecting the
  // current theme neither re-renders nor writes to storage.
  const setTheme = useCallback((theme: Theme) => {
    setState((previous) => (previous.theme === theme ? previous : { ...previous, theme }));
  }, []);

  const setDensity = useCallback((density: Density) => {
    setState((previous) => (previous.density === density ? previous : { ...previous, density }));
  }, []);

  const setLocale = useCallback((locale: Locale) => {
    setState((previous) => (previous.locale === locale ? previous : { ...previous, locale }));
  }, []);

  const setAccent = useCallback((accent: Accent) => {
    setState((previous) => (previous.accent === accent ? previous : { ...previous, accent }));
  }, []);

  const setBackground = useCallback((background: Background) => {
    setState((previous) =>
      previous.background === background ? previous : { ...previous, background },
    );
  }, []);

  const value = useMemo<Appearance>(
    () => ({
      ...state,
      dir: directionFor(state.locale),
      setTheme,
      setDensity,
      setLocale,
      setAccent,
      setBackground,
    }),
    [state, setTheme, setDensity, setLocale, setAccent, setBackground],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}
